import React, { useEffect, useRef, useMemo, forwardRef, useImperativeHandle, useCallback, useState} from "react";
import { Group, Image as KonvaImage, Transformer, Rect, Line,  Text} from "react-konva";
import useImage from "../hooks/use-image";
import Konva from "konva"; //  This is fine
import type { Slot } from "@/types/Slot";
import { calculateSnapping, calculateGroupSnapping } from "../utils/snapUtils";


type KonvaGroup = Konva.Group;

type Props = {
    imageSrc: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
    imageScale?: number;
    imageRotation: number;
    onSelect: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
    onDragMove: (pos: { x: number; y: number }, evt?: MouseEvent) => void;
    slot: Slot;
    slots: Slot[];
    isSelected: boolean;
    onChange: (changes: Partial<Slot>) => void;
    onUpdateGuides: (guides: { points: number[] }[]) => void;
    stageHeight: number;
    stageWidth: number;
    registerSlotRef?: (id: string, node: KonvaGroup | null) => void;
    selectedIds: string[];
    onDropImage?: (slotId: string, imageUrl: string) => void;
    opacity?: number;
    referenceRects: { x: number; y: number; width: number; height: number }[]; // <-- unified snapping
    setHoveredSlotId: (id: string | null) => void;
    updateSlot: (id: string, newProps: Partial<Slot>) => void;
    onRightClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    editorMode: "auto" | "manual"; // new prop     
    albumType: string;
    isMultiSelecting?: boolean;
    onDragStart?: (e: Konva.KonvaEventObject<DragEvent>) => void;
    onDragEnd?: (e: Konva.KonvaEventObject<DragEvent>) => void;
    isTransformingRef?: React.MutableRefObject<boolean>;
    onGroupSnapMove?: (dx: number, dy: number) => void;
    groupBounds?: {
        x: number;
        y: number;
        width: number;
        height: number;
    } | null;
  

};

const KonvaSlot = forwardRef<Konva.Group, Props>((props, ref) => {
    const {
        slot,
        imageSrc,
        x,
        y,
        width,
        height,
        rotation,
        isSelected,
        onSelect,
        onChange,
        onUpdateGuides,
        stageHeight,
        stageWidth,
        selectedIds,
       // onDropImage,
        opacity,
        onRightClick,
        referenceRects,
        updateSlot,
        editorMode,
        setHoveredSlotId,
        albumType,
        registerSlotRef,
     //   isMultiSelecting,
        onDragStart,
      //  onDragEnd,
        groupBounds,
       // onGroupSnapMove,
        
    } = props;


    const groupRef = useRef<Konva.Group>(null);
    const rectRef = useRef<Konva.Rect>(null);
    const SNAP_THRESHOLD = 6;
    const minSize = 1;
    const [image] = useImage(imageSrc);
    const transformerRef = useRef<Konva.Transformer>(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [hoverRect, setHoverRect] = React.useState<{ x: number; y: number }[] | null>(null);
    const showWarning = slot.quality?.ok === false;


    //hover rect
    React.useEffect(() => {
        if (isHovered && groupRef.current) {
            const node = groupRef.current;
            const halfStroke = 0.5;

            const w = slot.width;
            const h = slot.height;

            const cx = node.x();
            const cy = node.y();
            const rot = Konva.getAngle(node.rotation() || 0);
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);

            const corners = [
                { x: 0 - halfStroke, y: 0 - halfStroke },
                { x: w + halfStroke, y: 0 - halfStroke },
                { x: w + halfStroke, y: h + halfStroke },
                { x: 0 - halfStroke, y: h + halfStroke },
            ].map(p => ({
                x: cx + p.x * cos - p.y * sin,
                y: cy + p.x * sin + p.y * cos,
            }));

            setHoverRect(corners);
        } else {
            setHoverRect(null);
        }
    }, [isHovered, slot.width, slot.height, slot.x, slot.y, slot.rotation]);


    // Expose the internal groupRef to the parent via forwarded ref
   useImperativeHandle(ref, () => groupRef.current! , []);



    const crop = useMemo(() => {
        if (!image || !stageHeight) return null;

        const imageRatio = image.width / image.height;
        const frameRatio = width / height;

        let cropWidth: number;
        let cropHeight: number;

        // --- COVER LOGIC ---
        if (frameRatio > imageRatio) {
            cropWidth = image.width;
            cropHeight = image.width / frameRatio;
        } else {
            cropHeight = image.height;
            cropWidth = image.height * frameRatio;
        }

        // --- APPLY ZOOM ---
        const zoom = slot.imageZoom ?? 1;
        cropWidth /= zoom;
        cropHeight /= zoom;

        // --- CENTER (zoomed or default) ---
        const centerX = slot.imageCenter?.x ?? image.width / 2;
        const centerY = slot.imageCenter?.y ?? image.height / 2;

        return {
            x: Math.max(0, Math.min(image.width - cropWidth, centerX - cropWidth / 2)),
            y: Math.max(0, Math.min(image.height - cropHeight, centerY - cropHeight / 2)),
            width: cropWidth,
            height: cropHeight,
        };
    }, [image, width, height, stageHeight, slot.imageZoom, slot.imageCenter]);



    const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
        const node = e.target;

        // Single image
        if (selectedIds.length === 1) {
            const movingRect = {
                x: node.x(),
                y: node.y(),
                width,
                height,
            };

            const { x: snappedX, y: snappedY, guides } = calculateSnapping({
                movingRect,
                referenceRects,
                stageSize: {
                    width: stageWidth,
                    height: stageHeight,
                },
                snapThreshold: SNAP_THRESHOLD,
            });

            node.x(snappedX);
            node.y(snappedY);

            onUpdateGuides?.(guides);
            return;
        }


        // Group
        if (selectedIds.length > 1 && groupBounds) {

            const { x: snappedX, y: snappedY, guides } = calculateGroupSnapping({
                groupBounds,
                stageSize: {
                    width: stageWidth,
                    height: stageHeight,
                },
                snapThreshold: SNAP_THRESHOLD,
            });

            const deltaX = snappedX - groupBounds.x;
            const deltaY = snappedY - groupBounds.y;


            if (deltaX !== 0 || deltaY !== 0) {
                const transformer = transformerRef.current;

                if (transformer) {
                    transformer.nodes().forEach(node => {
                        node.x(node.x() + deltaX);
                        node.y(node.y() + deltaY);
                    });

                    transformer.getLayer()?.batchDraw();
                }
            }


            onUpdateGuides?.(guides);
            return;
        }
    }


    // Handle drag over to allow drop
    const handleDragOver = (e: Konva.KonvaEventObject<DragEvent>) => {
        (e.evt as DragEvent).preventDefault();
    };


    const imageRef = useRef<Konva.Image>(null);

    // Compute Konva filters based on slot properties
    const getAppliedFilters = useCallback((slot: Slot) => {
        const filters: any[] = [];
        if (slot.blackWhite) filters.push(Konva.Filters.Grayscale);
        return filters;
    }, []); // stable reference

    const strokeWidth = Math.max(0, slot.strokeWidth ?? 0);
    const strokeColor = slot.strokeColor ?? "#000";

    // Apply filters whenever the image or slot changes
    useEffect(() => {
        const imgNode = imageRef.current;
        if (!imgNode) return;

        const filters = getAppliedFilters(slot);

        imgNode.clearCache();
        if (filters.length > 0) {
            imgNode.filters(filters);
            imgNode.cache({ padding: 0 } as any);
        } else {
            imgNode.filters([]);
            imgNode.clearCache();
        }

        imgNode.getLayer()?.batchDraw();
    }, [image, slot, getAppliedFilters]);

  

    const onTransformEnd = () => {
        const node = groupRef.current;
        if (!node) return;

        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        node.scaleX(1);
        node.scaleY(1);

        const newWidth = Math.max(minSize, width * scaleX);
        const newHeight = Math.max(minSize, height * scaleY);

        // If there's an image, adjust it too
        if (imageRef.current) {
            const img = imageRef.current;

            img.clearCache();
            img.width(newWidth);
            img.height(newHeight);

            const filters = getAppliedFilters(slot);

            if (filters.length > 0) {
                img.filters(filters);
                img.cache({ padding: 0 } as any);
            } else {
                img.clearCache();
            }
        }

        onChange({
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            rotation: node.rotation(),
        });

        onUpdateGuides([]);
    };


  
    // Resize the image with the status bar controls
    useEffect(() => {
        if (imageRef.current) {
            const img = imageRef.current;

            img.width(width);
            img.height(height);

            const filters = getAppliedFilters(slot);

            if (filters.length > 0) {
                img.filters(filters);
                img.cache({ padding: 0 } as any);
            } else {
                img.clearCache();
            }
        }
    }, [width, height, slot, getAppliedFilters]);

    //new code for zoom
    const [zoomEdit, setZoomEdit] = useState<{
        slotId: string;
        imageX: number;
        imageY: number;
        imageScale: number;
    } | null>(null);


    const zoomImageRef = useRef<Konva.Image>(null);
    const zoomDataRef = useRef<{
        x: number;
        y: number;
        scale: number;
        renderScale: number;
        imageCenterX?: number;
        imageCenterY?: number;
    }>({
        x: 0,
        y: 0,
        scale: 1,
        renderScale: 1,
        imageCenterX: undefined,
        imageCenterY: undefined,
    });



    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            // Prevent page scrolling when zooming an image
            if (zoomEdit) {
                e.preventDefault();
            }
        };

        window.addEventListener("wheel", handleWheel, { passive: false });
        return () => {
            window.removeEventListener("wheel", handleWheel);
        };
    }, [zoomEdit]);

    // Works for zoom and for pan
    const handleExitZoom = () => {
        if (!image || !zoomDataRef.current) return;

        const { x: offsetX, y: offsetY, scale, renderScale } = zoomDataRef.current;

        //  Size of image after COVER scaling (before zoom)
        const coverScale = renderScale;
        const coveredWidth = image.width * coverScale;
        const coveredHeight = image.height * coverScale;

        //  Cover overflow (because of "cover" behavior)
        const coverOffsetX = Math.max(0, (coveredWidth - slot.width) / 2);
        const coverOffsetY = Math.max(0, (coveredHeight - slot.height) / 2);

        //  Convert pan offsets back to COVER space center
        // offsetX/Y are how much the image moved relative to slot center
        const centerInCoverX =
            coveredWidth / 2 - (offsetX / scale) - coverOffsetX;

        const centerInCoverY =
            coveredHeight / 2 - (offsetY / scale) - coverOffsetY;

        //  Convert COVER space  RAW image pixels
        const imageCenterX = centerInCoverX / coverScale;
        const imageCenterY = centerInCoverY / coverScale;

        updateSlot(slot.id, {
            imageZoom: scale,
            imageCenter: {
                x: Math.max(0, Math.min(image.width, imageCenterX)),
                y: Math.max(0, Math.min(image.height, imageCenterY)),
            },
        });

        setZoomEdit(null);
    };



    const handleImageDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
        if (!image) return;

        const node = e.target;

        // Keep the node visually centered
        node.x(width / 2);
        node.y(height / 2);

        // Compute crop dimensions (cover mode)
        const imageRatio = image.width / image.height;
        const slotRatio = width / height;

        let cropWidth: number;
        let cropHeight: number;

        if (slotRatio > imageRatio) {
            cropWidth = image.width;
            cropHeight = image.width / slotRatio;
        } else {
            cropHeight = image.height;
            cropWidth = image.height * slotRatio;
        }

        // Apply zoom
        const zoom = slot.imageZoom ?? 1;
        cropWidth /= zoom;
        cropHeight /= zoom;

        // Movement in slot pixels  image-space
        const dx = e.evt.movementX * (cropWidth / width);
        const dy = e.evt.movementY * (cropHeight / height);

        if (!slot.imageCenter) slot.imageCenter = { x: image.width / 2, y: image.height / 2 };

        slot.imageCenter = {
            x: Math.max(cropWidth / 2, Math.min(image.width - cropWidth / 2, slot.imageCenter.x + dx)),
            y: Math.max(cropHeight / 2, Math.min(image.height - cropHeight / 2, slot.imageCenter.y + dy)),
        };
    };

    // Inside your component
    // ---- Animate the border inside your component ----
    const borderRef = useRef<Konva.Rect | null>(null);

    useEffect(() => {
        if (!zoomEdit) return;

        let offset = 0;

        const interval = setInterval(() => {
            if (!borderRef.current) return; //  critical fix

            offset -= 2;
            borderRef.current.dashOffset(offset);
            borderRef.current.getLayer()?.batchDraw();
        }, 30);

        return () => clearInterval(interval);
    }, [zoomEdit]);


    //Accepts zoom on enter
    useEffect(() => {
        if (!zoomEdit) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Enter") {
                handleExitZoom();
            }
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [zoomEdit, image]);

   

    const [zoomActiveSlotId, setZoomActiveSlotId] = useState<string | null>(null);
    const [showZoomHint, setShowZoomHint] = useState(true);

    const [zoomInImg, setZoomInImg] = useState<HTMLImageElement | null>(null);
    const [zoomOutImg, setZoomOutImg] = useState<HTMLImageElement | null>(null);
    const ZOOM_STEP = 0.05;
    const [warningImg, setWarningImg] = useState<HTMLImageElement | null>(null);
   

    useEffect(() => {
        const loadImage = (src: string) =>
            new Promise<HTMLImageElement>((resolve) => {
                const img = new window.Image();
                img.src = src;
                img.onload = () => resolve(img);
            });

        loadImage(`${process.env.PUBLIC_URL}/zoom_in.png`).then(setZoomInImg);
        loadImage(`${process.env.PUBLIC_URL}/zoom_out.png`).then(setZoomOutImg);
        loadImage(`${process.env.PUBLIC_URL}/warning.png`).then(setWarningImg); //  add this
    }, []);

 

    useEffect(() => {
        if (!zoomEdit?.slotId) return;

        setShowZoomHint(true);

        const t = setTimeout(() => {
            setShowZoomHint(false);
        }, 2500);

        return () => clearTimeout(t);
    }, [zoomEdit?.slotId]);

    ////this is for multi slot transformer
      // Transformer binding

    useEffect(() => {
        registerSlotRef?.(slot.id, groupRef.current);

        return () => {
            registerSlotRef?.(slot.id, null);
        };
    }, [registerSlotRef, slot.id]);


    const getSlotUIConfig = (productType: string) => {
        switch (productType) {

            case "ARCH":
                return {
                    warningOffsetX: 60,
                    warningOffsetY: 60,
                    zoomButtonY: 75,
                };
            case "FMOUNTS":
                return {
                    warningOffsetX: 50,
                    warningOffsetY: 50,
                    zoomButtonY: 75,
                };
            case "FPRINTS":
                return {
                    warningOffsetX: 55,
                    warningOffsetY: 55,
                    zoomButtonY: 75,
                };
            case "HANDTORN":
                return {
                    warningOffsetX: 40,
                    warningOffsetY: 40,
                    zoomButtonY: 75,
                };
            case "MONTAGE":
                return {
                    warningOffsetX: 45,
                    warningOffsetY: 45,
                    zoomButtonY: 75,
                };
            case "CANVAS":
                return {
                    warningOffsetX: 70,
                    warningOffsetY: 70,
                    zoomButtonY: 170,
                };
            case "FCANVAS":
                return {
                    warningOffsetX: 70,
                    warningOffsetY: 70,
                    zoomButtonY: 170,
                };

            case "PRINTS":
                return {
                    warningOffsetX: 70,
                    warningOffsetY: 70,
                    zoomButtonY: 75,
                };
            case "HIPSTER":
                return {
                    warningOffsetX: 40,
                    warningOffsetY: 40,
                    zoomButtonY: 75,
                };

            // Albums
            case "BOUTIQUE":
            case "LIFESTYLE":
            case "PORTRAIT":
            case "JOURNAL":
            case "GUEST":
            case "LITE":
            case "LUXE":
            default:
                return {
                    warningOffsetX: 45,
                    warningOffsetY: 45,
                    zoomButtonY: 75,
                };
        }
    };
    const ui = getSlotUIConfig(albumType);

    return (
        <>
            {/* Slot Group — main transformable container */}
            <Group
                id={slot.id}
                ref={groupRef}
                x={x}
                y={y}
                rotation={rotation}
                width={width}
                height={height}
                draggable={editorMode === "manual"}  
                onClick={onSelect}
                onTap={onSelect}
              
                onDragStart={(e) => {
                     if (selectedIds.length > 1) {
                      onDragStart?.(e);
                } else {
                   onSelect(e);
                   }
                   }}
                onDragMove={handleDragMove}
                onDragEnd={() => {
                    if (selectedIds.length > 1) {
                        onUpdateGuides?.([]);
                        return;
                    }

                    onChange({
                        x: groupRef.current?.x() ?? x,
                        y: groupRef.current?.y() ?? y,
                    });

                    onUpdateGuides?.([]);
                }}
                onTransformEnd={onTransformEnd}             
                onMouseLeave={() => setIsHovered(false)}
		
                onDragOver={handleDragOver}
                onContextMenu={(e) => {
                    e.evt.preventDefault();
                    onRightClick?.(e);
                }}
                onDblClick={() => {
                    if (!image) return;
                    setZoomActiveSlotId(slot.id);
                    if (zoomEdit?.slotId === slot.id) {
                        handleExitZoom();
                        return;
                    }

                    const scale = slot.imageZoom ?? 1;

                    // COVER scale (must match exit)
                    const renderScale = Math.max(
                        slot.width / image.width,
                        slot.height / image.height
                    );

                    const coveredWidth = image.width * renderScale;
                    const coveredHeight = image.height * renderScale;

                    const coverOffsetX = Math.max(0, (coveredWidth - slot.width) / 2);
                    const coverOffsetY = Math.max(0, (coveredHeight - slot.height) / 2);

                    let offsetX = 0;
                    let offsetY = 0;

                    if (slot.imageCenter) {
                        // RAW image center  COVER space
                        const centerInCoverX = slot.imageCenter.x * renderScale;
                        const centerInCoverY = slot.imageCenter.y * renderScale;

                        // COVER  pan offsets (inverse of exit)
                        offsetX =
                            (coveredWidth / 2 - coverOffsetX - centerInCoverX) * scale;

                        offsetY =
                            (coveredHeight / 2 - coverOffsetY - centerInCoverY) * scale;
                    } else {
                        // First time: visually centered
                        offsetX = -coverOffsetX;
                        offsetY = -coverOffsetY;
                    }

                    zoomDataRef.current = { scale, renderScale, x: offsetX,y: offsetY,};

                    setZoomEdit({slotId: slot.id,imageX: 0,imageY: 0, imageScale: 1, })

                    if (zoomImageRef.current) {
                        zoomImageRef.current.width(image.width * renderScale);
                        zoomImageRef.current.height(image.height * renderScale);
                        zoomImageRef.current.x(slot.width / 2 + offsetX);
                        zoomImageRef.current.y(slot.height / 2 + offsetY);
                    }
                }}
            >
                {/* Background + shadow */}
                <Rect
                    ref={rectRef}
                    width={width}
                    height={height}
                    fill={image ? "#fff" : "#e0e0e0"}
                    shadowColor={slot.shadowColor || "black"}
                    shadowBlur={slot.shadowBlur ?? 0}
                    shadowOffset={{ x: slot.shadowOffsetX ?? 0, y: slot.shadowOffsetY ?? 0 }}
                    shadowOpacity={slot.shadowOpacity ?? 0}
                    opacity={opacity ?? 1}
                />

                {/* Compute image size to fill slot while preserving aspect ratio */}
                {crop && image && zoomEdit?.slotId !== slot.id && (
                    <>
                        <KonvaImage
                            ref={imageRef}
                            image={image}
                            width={width}
                            height={height}
                            x={Math.round(width / 2)}  // center
                            y={Math.round(height / 2)}
                            offsetX={Math.round(width / 2)}
                            offsetY={Math.round(height / 2)}                          
                            scaleX={slot.scaleX ?? 1}
                            scaleY={slot.scaleY ?? 1}
                         //   rotation={slot.imageRotation ?? 0}
                            crop={crop}
                            opacity={opacity ?? 1}                   
                            listening={true}
                            onDragMove={handleImageDragMove}
                        />
                        {strokeWidth > 0 && (
                            <Rect
                                x={strokeWidth / 2 - 0.5}
                                y={strokeWidth / 2 - 0.5}
                                width={width - strokeWidth + 1}
                                height={height - strokeWidth + 1}
                                stroke={strokeColor}
                                strokeWidth={strokeWidth}
                                fillEnabled={false}
                                listening={false}
                                opacity={opacity ?? 1}
                            />
                        )}
                    </>
                )}
               
                {showWarning && warningImg && (
                    <Group
                 
                        x={slot.width - ui.warningOffsetX}
                        y={slot.height - ui.warningOffsetY}
                        onMouseEnter={() => {                        
                            setHoveredSlotId(slot.id);
                        }}
                        onMouseLeave={() => setHoveredSlotId(null)}
                    >
                         {/*Invisible hit area (bigger + reliable) */}
                        <Rect width={30} height={30} opacity={0} />

                         {/*Actual icon (no events) */}
                        <KonvaImage
                            image={warningImg}
                            width={24}
                            height={24}
                            listening={false}
                        />
                    </Group>
                )}

          
                {selectedIds.length ===2  && selectedIds.includes(slot.id) && (
                    <Rect
                        x={0}
                        y={0}
                        width={slot.width}
                        height={slot.height}
                        stroke="red"
                        strokeWidth={4}
                        listening={false}
                    />
                )}

            </Group>

   
            {/* Zoom overlay */}
            {zoomEdit?.slotId === slot.id && image && (
                <>
                    {/* Invisible layer to detect clicks outside the slot */}
                    <Rect
                        x={0}
                        y={0}
                        width={stageWidth}
                        height={stageHeight}
                        fill="rgba(0,0,0,0)" // fully transparent                       
                        onClick={handleExitZoom}
                    />
                  
                    {/* Animated blue border showing slot boundaries */}
                    <Rect
                        x={slot.x}
                        y={slot.y}
                        width={slot.width}
                        height={slot.height}
                        stroke="#59b2d8"
                        strokeWidth={8}
                        dash={[10, 5]} // dash pattern
                        ref={borderRef}
                        listening={false}
                    />
                
                    {/* Clipped group for zoomed image */}
                    <Group
                        x={slot.x}
                        y={slot.y}
                        width={slot.width}
                        height={slot.height}
                      //  clip={{ x: 0, y: 0, width: slot.width, height: slot.height }}
                    >
                        <KonvaImage
                            ref={zoomImageRef}
                            image={image}
                            width={width}
                            height={height}                        
                            x={width / 2 + zoomDataRef.current.x}
                            y={height / 2 + zoomDataRef.current.y}
                            offsetX={width / 2}
                            offsetY={height / 2}
                            scaleX={zoomDataRef.current.scale}
                            scaleY={zoomDataRef.current.scale}
                            onMouseDown={(e) => e.cancelBubble = true}
                            onTouchStart={(e) => e.cancelBubble = true}
                            draggable
                            onDragMove={(e) => {
                                const node = e.target;
                                const zoomScale = zoomDataRef.current.scale;
                                const imgWidth = node.width() * zoomScale;
                                const imgHeight = node.height() * zoomScale;

                                const left = node.x() - node.offsetX() * zoomScale;
                                const top = node.y() - node.offsetY() * zoomScale;

                                const minX = slot.width - imgWidth;
                                const maxX = 0;
                                const minY = slot.height - imgHeight;
                                const maxY = 0;

                                const dx = Math.max(minX, Math.min(maxX, left));
                                const dy = Math.max(minY, Math.min(maxY, top));

                                node.x(dx + node.offsetX() * zoomScale);
                                node.y(dy + node.offsetY() * zoomScale);

                                zoomDataRef.current.x = node.x() - slot.width / 2;
                                zoomDataRef.current.y = node.y() - slot.height / 2;
                            }}
                         
                        />
                        <Group>
                            {(() => {
                                const size = 30;
                                const gap = 5;
                                const centerX = slot.width / 2;
                                const y = slot.height - ui.zoomButtonY;
                                const totalWidth = size * 2 + gap;
                                const startX = (slot.width - totalWidth) / 2;
                             

                                return (
                                    <>
                                        {/* Zoom OUT */}
                                        {zoomOutImg && (
                                            <KonvaImage
                                                image={zoomOutImg}
                                                x={startX}
                                                y={y}
                                                width={size}
                                                height={size}
                                                onClick={(e) => {
                                                    e.cancelBubble = true;

                                                    const node = zoomImageRef.current;
                                                    if (!node) return;

                                                    const newScale = Math.max(
                                                        1,
                                                        zoomDataRef.current.scale - ZOOM_STEP
                                                    );

                                                    // FULL RESET when back to normal size
                                                    if (newScale <= 1.01) {
                                                        zoomDataRef.current.scale = 1;
                                                        zoomDataRef.current.x = 0;
                                                        zoomDataRef.current.y = 0;

                                                        node.scale({ x: 1, y: 1 });

                                                        node.x(slot.width / 2);
                                                        node.y(slot.height / 2);

                                                        return;
                                                    }

                                                    zoomDataRef.current.scale = newScale;

                                                    node.scale({
                                                        x: newScale,
                                                        y: newScale,
                                                    });
                                                }}
                                                onMouseEnter={() => {
                                                    document.body.style.cursor = "pointer";
                                                }}
                                                onMouseLeave={() => {
                                                    document.body.style.cursor = "default";
                                                }}
                                            />
                                        )}

                                        {/* Zoom IN */}
                                        {zoomInImg && (
                                            <KonvaImage
                                                image={zoomInImg}
                                                x={startX + size + gap}
                                                y={y}
                                                width={size}
                                                height={size}
                                                onClick={(e) => {
                                                    e.cancelBubble = true;

                                                    const newScale = Math.max(
                                                        1,
                                                        zoomDataRef.current.scale + ZOOM_STEP
                                                    );

                                                    zoomDataRef.current.scale = newScale;

                                                    zoomImageRef.current?.scale({
                                                        x: newScale,
                                                        y: newScale,
                                                    });
                                                }}
                                                onMouseEnter={() => {
                                                    document.body.style.cursor = "pointer";
                                                }}
                                                onMouseLeave={() => {
                                                    document.body.style.cursor = "default";
                                                }}
                                            />
                                        )}
                                        <Group
                                            x={centerX}
                                            y={y - 28}
                                            listening={false}
                                        >
                                            <Rect
                                                x={-110}
                                                y={0}
                                                width={220}
                                                height={20}
                                                fill="rgba(255,0,0,0.6)"
                                                cornerRadius={6}
                                            />
                                            <Text
                                                x={-95}
                                                y={4}
                                                text="Press Enter to apply zoom & pan"
                                                fontSize={13}
                                                fill="white"
                                            />
                                        </Group>
                                    </>
                                );
                            })()}
                        </Group>
                    </Group>
                  
                </>
            )}

      
            {/* Hover rect */}
            {hoverRect && !isSelected && (
                <Line
                    points={hoverRect.flatMap((p) => [p.x, p.y])}
                    stroke="#59b2d8"
                    strokeWidth={2}
                    closed
                    listening={false}
                />
            )}
           
        </>
    );


})
export default KonvaSlot;